import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { PlanFigure } from '@oscom/shared';

/**
 * A plan in a document: the image scaled to the column, markers NEUTRAL
 * (number or beacon name, no traffic light) at their relative position.
 * If the image cannot be loaded (e.g. plan feature switched off), a notice
 * replaces it and the markers are dropped. Callers key it on the image asset,
 * so a new image starts without the failed state of the old one.
 */
export function PlanFigureView({
  figure,
  imageUrl,
  href,
}: {
  figure: PlanFigure;
  imageUrl: string;
  href?: string;
}): ReactNode {
  const [failed, setFailed] = useState(false);
  return (
    <figure className="plan-figure">
      {failed ? (
        <p className="notice notice--warning">
          Der Plan „{figure.name}“ konnte nicht geladen werden.
        </p>
      ) : (
        <div
          className="plan-figure__frame"
          style={{ aspectRatio: `${figure.image.width} / ${figure.image.height}` }}
        >
          {/* No lazy loading: print and PDF must contain every plan. */}
          <img src={imageUrl} alt={`Plan ${figure.name}`} onError={() => setFailed(true)} />
          {figure.markers.map((marker) => (
            <span
              key={`${marker.kind}-${marker.refId}`}
              className={`plan-figure__marker plan-figure__marker--${marker.kind}`}
              style={{ left: `${marker.x * 100}%`, top: `${marker.y * 100}%` }}
              title={marker.kind === 'point' ? `Messpunkt ${marker.label}` : marker.label}
            >
              {marker.label}
            </span>
          ))}
        </div>
      )}
      <figcaption>{href ? <Link to={href}>{figure.name}</Link> : figure.name}</figcaption>
    </figure>
  );
}
